import React, { memo, useState, useEffect } from 'react';
import './MobileViewToggle.css';

const MobileViewToggle = memo(({ currentView, onToggle, featureFlags = {} }) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1025);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1025);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTouchStart = (e) => {
    // Prevent default to avoid scrolling while interacting
    e.currentTarget.style.touchAction = 'manipulation';
  };

  const handleTouchEnd = (e) => {
    // Add haptic feedback on iOS
    if (window.navigator.vibrate) {
      window.navigator.vibrate(10);
    }
  };

  // Determine if multiview should be enabled (all three features must be enabled)
  const isMultiviewEnabled = featureFlags.camerControlEnabled && featureFlags.telemetryEnabled && featureFlags.mapEnabled;
  
  // Determine if split screen should be enabled (map and camera must be enabled)
  const isSplitScreenEnabled = featureFlags.camerControlEnabled && featureFlags.mapEnabled;

  return (
    <div className="mobile-view-toggle">
      <button 
        className={`toggle-button ${currentView === 'map' ? 'active' : ''}`}
        onClick={() => onToggle('map')}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        aria-label="Switch to Map View"
        aria-pressed={currentView === 'map'}
        disabled={!featureFlags.mapEnabled}
        title={!featureFlags.mapEnabled ? 'Map disabled by stream owner' : 'Switch to Map View'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
          <line x1="8" y1="2" x2="8" y2="18"></line>
          <line x1="16" y1="6" x2="16" y2="22"></line>
        </svg>
        <span>Map</span>
      </button>
      <button 
        className={`toggle-button ${currentView === 'camera' ? 'active' : ''}`}
        onClick={() => onToggle('camera')}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        aria-label="Switch to Camera View"
        aria-pressed={currentView === 'camera'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
        <span>Camera</span>
      </button>
      {/* Telemetry - Mobile/Tablet only */}
      {!isDesktop && (
        <button 
          className={`toggle-button ${currentView === 'telemetry' ? 'active' : ''}`}
          onClick={() => onToggle('telemetry')}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          aria-label="Switch to Telemetry View"
          aria-pressed={currentView === 'telemetry'}
          disabled={!featureFlags.telemetryEnabled}
          title={!featureFlags.telemetryEnabled ? 'Telemetry disabled by stream owner' : 'Switch to Telemetry View'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="3" y1="9" x2="21" y2="9"></line>
            <line x1="9" y1="21" x2="9" y2="9"></line>
          </svg>
          <span>Telemetry</span>
        </button>
      )}
      
      {/* Multiview - Mobile/Tablet only */}
      {!isDesktop && (
        <button 
          className={`toggle-button ${currentView === 'multiview' ? 'active' : ''}`}
          onClick={() => onToggle('multiview')}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          aria-label="Switch to Multiview"
          aria-pressed={currentView === 'multiview'}
          disabled={!isMultiviewEnabled}
          title={!isMultiviewEnabled ? 'Multiview requires all features (Map, Camera, Telemetry) to be enabled' : 'Switch to Multiview'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
          </svg>
          <span>Multiview</span>
        </button>
      )}
      
      {/* Split Screen - Desktop only */}
      {isDesktop && (
        <button 
          className={`toggle-button ${currentView === 'splitscreen' ? 'active' : ''}`}
          onClick={() => onToggle('splitscreen')}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          aria-label="Switch to Split Screen"
          aria-pressed={currentView === 'splitscreen'}
          disabled={!isSplitScreenEnabled}
          title={!isSplitScreenEnabled ? 'Split Screen requires both Map and Camera to be enabled' : 'Switch to Split Screen'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="8" height="18" rx="2"></rect>
            <rect x="13" y="3" width="8" height="18" rx="2"></rect>
          </svg>
          <span>Split Screen</span>
        </button>
      )}
    </div>
  );
});

MobileViewToggle.displayName = 'MobileViewToggle';

export default MobileViewToggle; 